import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouterProviderProps } from 'react-router-dom';
import App from './App.js';
import Host from './routes/Host.js';
import Play from './routes/Play.js';
import Stage from './routes/Stage.js';
import { isEmbedded } from './discord/embedded.js';

/**
 * Router — all routes wired to their components:
 *   /            → Store (landing page; responsive — desktop + mobile)
 *   /host        → Stage (the Ringmaster's device)
 *   /play        → Hand
 *   /stage/:code → pure spectator/broadcast surface (TV / Discord Activity viewer)
 *   /activity    → Discord Embedded App surface (?discordmock=1 → plain-browser demo)
 *   /studio      → dev-only audio/text tuning studio (404s the manifest in prod)
 *   /admin       → token-gated internal ops dashboard (analytics; no game driver)
 *
 * The landing renders on every device. Its CTAs route the user onward:
 * "Join a lobby" → /play, "Start a new lobby" → /host.
 *
 * Route-splitting: Store (1000+ lines of carousel), Studio, and CardLab are
 * lazy-loaded so players deep-linking /play or /stage don't download them.
 * Host / Play / Stage stay eager — they're the hot paths a fresh device hits.
 */

// Heavy / rarely-hit routes are code-split behind React.lazy.
const Store = lazy(() => import('./routes/Store.js'));
const Studio = lazy(() => import('./routes/Studio.js'));
const CardLab = lazy(() => import('./routes/CardLab.js'));
const Admin = lazy(() => import('./routes/Admin.js'));
// The Discord Activity surface (+ the Embedded SDK) is only pulled in when a
// player actually opens /activity — split so /play and /stage stay lean.
const Activity = lazy(() => import('./routes/Activity.js'));

/** True when the current device looks like a phone (small screen / mobile UA). */
export function isPhone(): boolean {
  if (typeof window === 'undefined') return false;
  const narrow = window.matchMedia?.('(max-width: 820px)').matches ?? false;
  const mobileUA = /Android|iPhone|iPod|Mobile/i.test(navigator.userAgent);
  return narrow || mobileUA;
}

/** Minimal Suspense fallback: an ink background (never a flash of white). */
function Fallback() {
  return <div style={{ minHeight: '100dvh', background: 'var(--bg-base)' }} />;
}

/** Wrap a lazy route element in Suspense so its chunk can load. */
function lazyRoute(node: ReactNode): ReactNode {
  return <Suspense fallback={<Fallback />}>{node}</Suspense>;
}

export const router: RouterProviderProps['router'] = createBrowserRouter([
  {
    // Discord launches an Activity at the ROOT of the URL mapping — the launch
    // path is not configurable — so `/` must serve the Activity surface when
    // we're inside the Discord iframe. Plain browsers get the Store landing.
    path: '/',
    element: <App>{lazyRoute(isEmbedded() ? <Activity /> : <Store />)}</App>,
  },
  {
    path: '/host',
    element: (
      <App>
        <Host />
      </App>
    ),
  },
  {
    // Reconnect entry: `/host/:code` auto-REJOINs the host to an existing room
    // (a refresh lands here after CREATE_ROOM rewrites the URL). Same component.
    path: '/host/:code',
    element: (
      <App>
        <Host />
      </App>
    ),
  },
  {
    path: '/play',
    element: (
      <App>
        <Play />
      </App>
    ),
  },
  {
    // Pure spectator / broadcast surface for a live room (TV, Discord Activity).
    path: '/stage/:code',
    element: (
      <App>
        <Stage />
      </App>
    ),
  },
  {
    // Discord Embedded App surface (or ?discordmock=1 for a local browser demo).
    path: '/activity',
    element: <App>{lazyRoute(<Activity />)}</App>,
  },
  {
    path: '/studio',
    element: <App>{lazyRoute(<Studio />)}</App>,
  },
  {
    // Internal ops dashboard. Not linked from anywhere in the game UI; the server
    // gates every /api/admin/* call behind ADMIN_TOKEN, so the page itself is just
    // a shell until a valid token is entered.
    //
    // Splat, not a bare path: every tab and every drill-down is a real URL
    // (/admin/sessions, /admin/player/:id, …) so browser Back works and any row
    // can be middle-clicked into a new tab. Admin.tsx owns the nested <Routes>.
    path: '/admin/*',
    element: <App>{lazyRoute(<Admin />)}</App>,
  },
  {
    // Dev-only preview for the polished role card (flip / tilt / glare). Not
    // linked from anywhere in the game UI.
    path: '/card-lab',
    element: <App>{lazyRoute(<CardLab />)}</App>,
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
